import React, { useEffect, useRef, useState } from 'react';
import { MdStar } from "react-icons/md";
import { Reveal } from "semantic-ui-react";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import Services from "./Services";
import { motion, useScroll, useTransform } from "framer-motion";
import { s10, s11, s9 } from "../assets";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const FeatureCard = ({ slider }) => {
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const horizontalContainerRef = useRef(null);
  const progressBarRef = useRef(null);
  const lenisRef = useRef(null);
  const scrollTriggerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(-1); // -1 for Services
  const [isHorizontalActive, setIsHorizontalActive] = useState(false);
  const [currentSection, setCurrentSection] = useState('services'); // 'services', 'cards', or 'image'

  // For image parallax effect
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [-100, 100]); // Parallax movement
  const imageScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.2, 1, 1.2]); // Scale effect

  useEffect(() => {
    if (!sectionRef.current || !horizontalContainerRef.current) return;

    // Initialize Lenis for smooth scrolling
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 0.8,
      touchMultiplier: 1.5,
    });
    lenisRef.current = lenis;

    lenis.on("scroll", ScrollTrigger.update);

    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    // Calculate total horizontal scroll distance (Services + Cards + Image)
    const getScrollAmount = () => {
      const container = horizontalContainerRef.current;
      if (!container) return 0;
      return container.scrollWidth - window.innerWidth;
    };

    // Create the main ScrollTrigger for horizontal scroll
    scrollTriggerRef.current = ScrollTrigger.create({
      trigger: sectionRef.current,
      start: "top top",
      end: () => `+=${getScrollAmount()}`,
      pin: true,
      scrub: 1,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onEnter: () => setIsHorizontalActive(true),
      onLeave: () => setIsHorizontalActive(false),
      onEnterBack: () => setIsHorizontalActive(true),
      onLeaveBack: () => setIsHorizontalActive(false),
      onUpdate: (self) => {
        // Update horizontal scroll position
        if (horizontalContainerRef.current) {
          const scrollAmount = getScrollAmount();
          gsap.set(horizontalContainerRef.current, {
            x: -self.progress * scrollAmount
          });
        }

        // Update progress bar
        if (progressBarRef.current) {
          gsap.set(progressBarRef.current, {
            scaleX: self.progress
          });
        }

        // Calculate section boundaries
        const totalWidth = horizontalContainerRef.current.scrollWidth;
        const servicesWidth = window.innerWidth;
        const cardsWidth = slider.length * 860; // Approximate card width including gaps
        const imageWidth = window.innerWidth;
        
        const servicesEnd = servicesWidth / totalWidth;
        const cardsEnd = (servicesWidth + cardsWidth) / totalWidth;
        
        if (self.progress <= servicesEnd) {
          setCurrentSection('services');
          setActiveIndex(-1);
        } else if (self.progress <= cardsEnd) {
          setCurrentSection('cards');
          // Calculate card index
          const cardProgress = (self.progress - servicesEnd) / (cardsEnd - servicesEnd);
          const currentIndex = Math.round(cardProgress * (slider.length - 1));
          if (currentIndex !== activeIndex && currentIndex >= 0 && currentIndex < slider.length) {
            setActiveIndex(currentIndex);
          }
        } else {
          setCurrentSection('image');
          setActiveIndex(-1);
        }
      }
    });

    // Cleanup
    return () => {
      if (scrollTriggerRef.current) {
        scrollTriggerRef.current.kill();
      }
      if (lenisRef.current) {
        lenisRef.current.destroy();
      }
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [slider.length]);

  // Handle navigation click
  const handleNavigate = (index) => {
    if (scrollTriggerRef.current && horizontalContainerRef.current) {
      const totalWidth = horizontalContainerRef.current.scrollWidth;
      const scrollAmount = totalWidth - window.innerWidth;
      const servicesWidth = window.innerWidth;
      const cardsWidth = slider.length * 860;
      const imageWidth = window.innerWidth;
      
      const servicesEnd = servicesWidth / totalWidth;
      const cardsEnd = (servicesWidth + cardsWidth) / totalWidth;
      
      let targetProgress;
      if (index === -1) {
        // Navigate to Services
        targetProgress = 0;
      } else if (index === -2) {
        // Navigate to Image section
        targetProgress = cardsEnd + (1 - cardsEnd) / 2;
      } else {
        // Navigate to specific card
        const cardProgress = index / (slider.length - 1);
        targetProgress = servicesEnd + cardProgress * (cardsEnd - servicesEnd);
      }
      
      const targetScroll = targetProgress * scrollAmount;
      
      // Animate to the target position
      if (lenisRef.current) {
        lenisRef.current.scrollTo(targetScroll, {
          duration: 1.5,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        });
      }
    }
  };

  return (
    <div ref={sectionRef} className="relative w-full overflow-hidden bg-white">
      {/* Progress bar */}
      <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-50 w-64 h-1 bg-gray-200 rounded-full overflow-hidden">
        <div 
          ref={progressBarRef}
          className="h-full bg-gradient-to-r from-fourth via-third to-second origin-left scale-x-0 rounded-full"
        />
      </div>

      {/* Active indicator */}
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 text-sm font-medium text-gray-600">
        {currentSection === 'services' && (
          <span className="text-blue-600"></span>
        )}
        {currentSection === 'cards' && (
          <span>
            {String(activeIndex + 1).padStart(2, '0')} | {String(slider.length).padStart(2, '0')}
          </span>
        )}
      </div>

      {/* Horizontal container with Services, Cards, and Image */}
      <div 
        ref={horizontalContainerRef}
        className="flex items-stretch min-h-screen"
        style={{ 
          willChange: 'transform',
          width: 'max-content'
        }}
      >
        {/* Services Section */}
        <div className="w-screen min-h-screen flex-shrink-0 flex items-center justify-center relative">
          <Services />
          
          {/* Scroll indicator */}
          <div className="absolute bottom-14 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2">
            <p className="text-sm text-gray-500">Scroll to explore</p>
            <div className="w-10 h-6 border-2 border-gray-400 rounded-full flex justify-center">
              <div className="w-2 h-1 bg-gray-400 rounded-full animate-bounce mt-2 ml-4"></div>
            </div>
          </div>
        </div>

        {/* Cards Section */}
        {slider.map((item, index) => (
          <div
            key={item.number || index}
            onClick={() => handleNavigate(index)}
            className={`bg-fourth relative border border-second border-dashed mx-4 rounded-2xl overflow-hidden w-[800px] min-h-[80vh] flex flex-col justify-center flex-shrink-0 cursor-pointer transition-all duration-500 my-auto ${
              activeIndex === index 
                ? 'border-blue-500 shadow-2xl scale-105' 
                : 'opacity-80 hover:opacity-100 hover:scale-102'
            }`}
          >
            {/* Card number indicator */}
            <div className="absolute top-10 left-10 text-md font-bold text-second select-none">
              {String(index + 1).padStart(2, '0')}
            </div>

            <div className="py-4">
              <div className="py-6 px-10 flex flex-row items-start gap-10 h-full">
                <div className="flex flex-col gap-6 mt-4">
                  <Reveal>
                    {/* Title */}
                    <h2 className="text-white text-md xs:text-md font-bold tracking-tight leading-snug mb-2">
                      {item.name}
                    </h2>
                  </Reveal>
                  <Reveal>
                    {/* Subtitle */}
                    <p className="text-white/80 text-mm font-medium tracking-tight leading-snug my-3 max-w-[340px]">
                      {item.detail}
                    </p>
                  </Reveal>
                  <Reveal>
                    {/* Content description */}
                    <p className="text-white/80 text-ss md:text-sm tracking-tight leading-relaxed opacity-90 line-clamp-5 max-w-[340px]">
                      {item.content}
                    </p>
                  </Reveal>
                </div>
                <div className="flex flex-col gap-6 mt-4">
                  <Reveal>
                    <p className="text-second text-ss md:text-sm tracking-tight leading-relaxed font-medium underline line-clamp-4">
                      Includes:
                    </p>
                  </Reveal>
                  <Reveal>
                    <div className="flex flex-col gap-2">
                      {/* description items */}
                      <p className="text-white/80 text-ss md:text-sm tracking-tight leading-relaxed font-medium line-clamp-4">
                        {item.desc1}
                      </p>
                      <p className="text-white/80 text-ss md:text-sm tracking-tight leading-relaxed font-medium line-clamp-4">
                        {item.desc2}
                      </p>
                      <p className="text-white/80 text-ss md:text-sm tracking-tight leading-relaxed font-medium line-clamp-4">
                        {item.desc3}
                      </p>
                      <p className="text-white/80 text-ss md:text-sm tracking-tight leading-relaxed font-medium line-clamp-4">
                        {item.desc4}
                      </p>
                      <p className="text-white/80 text-ss md:text-sm tracking-tight leading-relaxed font-medium line-clamp-4">
                        {item.desc5}
                      </p>
                      <p className="text-white/80 text-ss md:text-sm tracking-tight leading-relaxed font-medium line-clamp-4">
                        {item.desc6}
                      </p>
                    </div>
                  </Reveal>
                </div>
              </div>
            </div>
          </div>
        ))} 
        
        {/* Image Section */}
        <div 
          ref={containerRef}
          className="relative w-screen h-screen flex-shrink-0 overflow-hidden"
          onClick={() => handleNavigate(-2)}
          style={{ background: '#f0f0f0' }}
        >
          {/* Image with parallax effect */}
          <motion.img 
            src={s9} 
            alt="Gallery" 
            className="w-full h-full object-cover absolute inset-0"
            style={{ 
              y: imageY,
              scale: imageScale,
              willChange: 'transform'
            }}
          />
          
          {/* Overlay content */}
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-primary text-mm font-bold mb-4">Our Outcomes</h2>
              <p className="text-secondary text-md font-semibold px-4">What schools gain from our programs</p>
            </div>
          </div>
          
          {/* Scroll indicator for end */}
          <div className="absolute bottom-14 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2">
            <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
              <div className="w-1 h-2 bg-white rounded-full animate-bounce mt-6"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureCard;